import { getLogger } from './logger';
import { createApp } from './main';
import { sentryStateKey } from './sentry';
import { getServerState } from './state';
if (window.__VITE_INIT_APP__ == null) {
    window.__VITE_INIT_APP__ = {};
}
window.__VITE_INIT_APP__[__APP_NAME__] = async (appId, isFragment) => {
    const appReturn = await createApp(appId);
    if (!appReturn) {
        getLogger().fatal('Page not found, this shouldn\'t be possible');
        return;
    }
    const sentryInfo = getServerState(sentryStateKey, appId);
    // There should only be one Sentry instance on a page, to prevent weird issues
    // This instance will then be used with a multiplex transport to route errors
    // from microfrontends to the correct Sentry DSN
    if (sentryInfo) {
        // this try/catch thing instead of static import fixes dev mode where
        // uBlock and the likes block sentry from being imported, causing the
        // script execution to fail.
        try {
            const Sentry = await import('@sentry/vue');
            if (isFragment === true) {
                appReturn.app.mixin(Sentry.createTracingMixins({ trackComponents: false, timeout: 500, hooks: [] }));
                Sentry.attachErrorHandler(appReturn.app, { attachErrorHandler: true, attachProps: true, logErrors: true, trackComponents: false, timeout: 500, hooks: [] });
            }
            else {
                const makeConsentFetchTransport = (...args) => {
                    const transport = Sentry.makeFetchTransport(...args);
                    return {
                        // returning a promise with an empty object indicates the event
                        // was filtered and thus not sent
                        async send(...args) {
                            if (window.location.search.includes('testcafe'))
                                return {};
                            const consents = window.getCookieConsent?.();
                            if (sentryInfo.environment === 'development' ||
                                window.location.host.startsWith('test') ||
                                window.location.hostname === 'localhost' ||
                                /^\/(portal|portaali)\//.test(window.location.pathname)) {
                                return await transport.send(...args);
                            }
                            if (consents?.statistics)
                                return await transport.send(...args);
                            return {};
                        },
                        flush(...args) {
                            return transport.flush(...args);
                        },
                    };
                };
                const transport = Sentry.makeMultiplexedTransport(makeConsentFetchTransport, args => {
                    const event = args.getEvent();
                    if (typeof event?.tags?.appName === 'string')
                        return window.__VITE_SENTRY_ROUTES__?.[event.tags.appName] ?? [];
                    else
                        return [];
                });
                Sentry.init({
                    app: appReturn.app,
                    // This is our fallback DSN
                    dsn: sentryInfo.dsn,
                    release: sentryInfo.release,
                    environment: sentryInfo.environment,
                    transport,
                    integrations: [
                        Sentry.browserTracingIntegration(),
                        Sentry.httpClientIntegration(),
                        Sentry.replayIntegration({
                            unblock: ['.sentry-unblock, [data-sentry-unblock]'],
                            unmask: ['.sentry-unmask, [data-sentry-unmask]'],
                            maskAllText: false,
                        }),
                    ],
                    logErrors: true,
                    tracesSampleRate: sentryInfo.samplingRates.tracesSampleRate ?? 0.1,
                    replaysOnErrorSampleRate: sentryInfo.samplingRates.replaysOnErrorSampleRate ?? 1.0,
                    replaysSessionSampleRate: sentryInfo.samplingRates.replaysSessionSampleRate ?? 0.1,
                    tracePropagationTargets: [
                        'localhost',
                        /https?:\/\/([^/:]+\.)?zmarta\.(se|no|fi|org)(:\d+)?/,
                        /https?:\/\/([^/:]+\.)?centum\.no(:\d+)?/,
                        /https?:\/\/([^/:]+\.)?freedomfinance\.se(:\d+)?/,
                        /https?:\/\/([^/:]+\.)?freedomrahoitus\.fi(:\d+)?/,
                        /https?:\/\/([^/:]+\.)?elskling\.(se|no)(:\d+)?/,
                    ],
                });
            }
        }
        catch (err) {
            getLogger().error({ err }, 'failed to initialise Sentry');
        }
    }
    appReturn.app.mount(`#app-${__APP_NAME__}-${appId}`);
};
if (window.__VITE_HYDRATE__ == null) {
    window.__VITE_HYDRATE__ = async () => {
        const failed = [];
        while ((window.__VITE_INIT_QUEUE__ ?? []).length > 0) {
            const [appName, appId, isFragment] = window.__VITE_INIT_QUEUE__.shift();
            try {
                if (window.__VITE_INIT_APP__?.[appName] != null) {
                    await window.__VITE_INIT_APP__[appName](appId, isFragment);
                }
                else {
                    getLogger().warn('window.__VITE_INIT_APP__ undefined for', { appName, appId });
                    failed.push([appName, appId, isFragment]);
                }
            }
            catch (err) {
                getLogger().error(err);
            }
        }
        window.__VITE_INIT_QUEUE__ ??= [];
        window.__VITE_INIT_QUEUE__.push(...failed);
    };
}
//# sourceMappingURL=entry-client.js.map